import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Check, ChevronDown } from "lucide-react";
import { COUNTRIES } from "@/data/countries";
import SiteFooter from "@/components/SiteFooter";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";

const ACCENT = "#4FA3D9";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TM = ({ nudge = "-0.08em" }) => <sup className="align-super text-[0.55em]" style={{ marginLeft: nudge }}>™</sup>;

const USER_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "parent_guardian", label: "Parent / Guardian" },
  { value: "educator", label: "Educator" },
  { value: "educational_institution", label: "School / Educational Institution" },
  { value: "organisation", label: "Organisation / Company" },
  { value: "researcher_professional", label: "Researcher / Professional" },
  { value: "partner_collaborator", label: "Partner / Collaborator" },
  { value: "other", label: "Other" },
];

const INTERESTS = [
  { value: "trying_adaptience", label: "Trying Adaptience™" },
  { value: "family", label: "Adaptience™ for my family" },
  { value: "education", label: "Adaptience™ for education" },
  { value: "organisation", label: "Adaptience™ for my organisation" },
  { value: "future_simulations", label: "Future simulations" },
  { value: "partnership_collaboration", label: "Partnership / Collaboration" },
  { value: "research_media", label: "Research / Media" },
  { value: "something_else", label: "Something else" },
];

const DETAIL_MAX = 100;

const labelCls = "mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-white/55";
const fieldCls =
  "w-full rounded-xl border border-white/12 bg-white/[0.03] px-4 py-3.5 text-base text-white outline-none transition-colors placeholder:text-white/35 focus:border-white/30";

const Select = ({ value, onChange, placeholder, options, testid }) => (
  <div className="relative">
    <select
      value={value}
      onChange={onChange}
      data-testid={testid}
      className={`${fieldCls} appearance-none pr-10 ${value ? "text-white" : "text-white/55"}`}
    >
      <option value="" disabled>{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value} className="bg-[#141414] text-white">{o.label}</option>
      ))}
    </select>
    <ChevronDown size={16} className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/40" />
  </div>
);

const CountrySelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = COUNTRIES.filter((c) => c.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="contact-country"
        className={`${fieldCls} flex items-center justify-between text-left ${value ? "text-white" : "text-white/55"}`}
      >
        <span className="truncate">{value || "Select your country"}</span>
        <ChevronDown size={16} className="ml-2 shrink-0 text-white/40" />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-white/12 bg-[#141414] shadow-2xl">
          <div className="p-2">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              data-testid="contact-country-search"
              className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none placeholder:text-white/30 focus:border-white/25"
            />
          </div>
          <div className="max-h-56 overflow-y-auto pb-2">
            {value && (
              <button type="button" onClick={() => { onChange(""); setOpen(false); setQ(""); }} className="block w-full px-4 py-2 text-left text-sm text-white/55 transition-colors hover:bg-white/[0.05]">
                Clear selection
              </button>
            )}
            {filtered.length === 0 ? (
              <p className="px-4 py-3 text-sm text-white/55">No matches</p>
            ) : (
              filtered.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => { onChange(c); setOpen(false); setQ(""); }}
                  className={`block w-full px-4 py-2 text-left text-sm transition-colors hover:bg-white/[0.06] ${value === c ? "text-[#4FA3D9]" : "text-white/80"}`}
                >
                  {c}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function Contact({ onBack, onPrivacy, onTerms, onContact }) {
  const [form, setForm] = useState({
    name: "", email: "", user_type: "", user_type_other_detail: "", interest: "", interest_other_detail: "",
    country: "", message: "", form_ref: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Changing away from Other / Something else clears any stale detail before submission.
  const setUserType = (e) => setForm((f) => ({ ...f, user_type: e.target.value, user_type_other_detail: e.target.value === "other" ? f.user_type_other_detail : "" }));
  const setInterest = (e) => setForm((f) => ({ ...f, interest: e.target.value, interest_other_detail: e.target.value === "something_else" ? f.interest_other_detail : "" }));

  const validate = () => {
    if (!form.name.trim() || !form.email.trim() || !form.user_type || !form.interest || !form.message.trim()) {
      return "Please complete all required fields.";
    }
    if (form.user_type === "other" && !form.user_type_other_detail.trim()) {
      return "Please tell us how you'd describe yourself.";
    }
    if (form.interest === "something_else" && !form.interest_other_detail.trim()) {
      return "Please tell us what you're interested in.";
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      return "Please enter a valid email address.";
    }
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        user_type_other_detail: form.user_type === "other" ? form.user_type_other_detail.trim() : null,
        interest_other_detail: form.interest === "something_else" ? form.interest_other_detail.trim() : null,
      };
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        let msg = "Something went wrong sending your message. Please try again.";
        if (res.status === 429) msg = "You've sent a few messages already. Please try again shortly.";
        try { const d = await res.json(); if (res.status === 422 && d?.detail) msg = d.detail; } catch {}
        throw new Error(msg);
      }
      setDone(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[100dvh] w-full overflow-y-auto bg-[#0a0a0a] text-white" data-testid="contact-page">
      <Seo
        title="Contact Adaptience | Get in Touch"
        description="Get in touch with the Adaptience team — ask a question, share feedback, or suggest a real-life situation you'd like to practise as an interactive simulation."
        canonical="/contact"
      />
      <div className="pointer-events-none fixed inset-x-0 top-0 h-[55vh]">
        <div className="absolute left-1/2 top-[-10vh] h-[45vh] w-[70vw] -translate-x-1/2 rounded-full blur-[150px]" style={{ background: "rgba(79,163,217,0.09)" }} />
      </div>

      {/* NAV */}
      <Navbar />

      <main className="relative z-10 mx-auto w-full max-w-2xl px-6 py-14 sm:px-10 sm:py-20">
        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="py-10 text-center"
            data-testid="contact-success"
          >
            <div className="mx-auto mb-8 flex h-14 w-14 items-center justify-center rounded-full border" style={{ borderColor: ACCENT, color: ACCENT }}>
              <Check size={24} strokeWidth={2.5} />
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Thank you for getting in touch.</h1>
            <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-white/60">
              We've received your message and will get back to you soon.
            </p>
            <button
              onClick={onBack}
              data-testid="contact-success-back"
              className="mt-10 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-transform active:scale-[0.98]"
              style={{ background: ACCENT }}
            >
              <ArrowLeft size={15} />
              Back to Adaptience<TM />
            </button>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <p className="mb-3 text-xs font-semibold uppercase tracking-[0.28em]" style={{ color: ACCENT }}>Adaptience<TM nudge="-0.34em" /></p>
            <h1 className="font-display text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">Get in touch</h1>
            <p className="mt-5 text-base leading-relaxed text-white/60">
              Whether you have a question, an idea, or want to explore how Adaptience<TM /> could be useful to you, we'd love to hear from you.
            </p>

            <form onSubmit={onSubmit} className="mt-10 space-y-6" noValidate data-testid="contact-form">
              <div>
                <label className={labelCls} htmlFor="c-name">Name *</label>
                <input id="c-name" name="name" autoComplete="name" type="text" value={form.name} onChange={set("name")} placeholder="Your name" className={fieldCls} data-testid="contact-name" />
              </div>

              <div>
                <label className={labelCls} htmlFor="c-email">Email *</label>
                <input id="c-email" name="email" autoComplete="email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={fieldCls} data-testid="contact-email" />
              </div>

              <div>
                <label className={labelCls}>I am a… *</label>
                <Select value={form.user_type} onChange={setUserType} placeholder="Select one" options={USER_TYPES} testid="contact-usertype" />
                {form.user_type === "other" && (
                  <div className="mt-4">
                    <label className={labelCls} htmlFor="c-usertype-detail">Please specify *</label>
                    <input id="c-usertype-detail" type="text" maxLength={DETAIL_MAX} value={form.user_type_other_detail} onChange={set("user_type_other_detail")} placeholder="How would you describe yourself?" className={fieldCls} data-testid="contact-usertype-detail" />
                  </div>
                )}
              </div>

              <div>
                <label className={labelCls}>I'm interested in… *</label>
                <Select value={form.interest} onChange={setInterest} placeholder="Select one" options={INTERESTS} testid="contact-interest" />
                {form.interest === "something_else" && (
                  <div className="mt-4">
                    <label className={labelCls} htmlFor="c-interest-detail">Please specify *</label>
                    <input id="c-interest-detail" type="text" maxLength={DETAIL_MAX} value={form.interest_other_detail} onChange={set("interest_other_detail")} placeholder="What are you interested in?" className={fieldCls} data-testid="contact-interest-detail" />
                  </div>
                )}
              </div>

              <div>
                <label className={labelCls}>Country (optional)</label>
                <CountrySelect value={form.country} onChange={(v) => setForm((f) => ({ ...f, country: v }))} />
              </div>

              <div>
                <label className={labelCls} htmlFor="c-message">Message *</label>
                <textarea id="c-message" name="message" value={form.message} onChange={set("message")} rows={7} placeholder="Tell us how we can help..." className={`${fieldCls} min-h-[160px] resize-y`} data-testid="contact-message" />
              </div>

              {/* Honeypot — neutral name, display:none, autofill-resistant; hidden from real users */}
              <div style={{ display: "none" }} aria-hidden="true">
                <label htmlFor="c-form-ref">Leave this field empty</label>
                <input id="c-form-ref" name="form_ref" type="text" tabIndex={-1} autoComplete="new-password" value={form.form_ref} onChange={set("form_ref")} data-testid="contact-hp" />
              </div>

              {error && <p className="text-sm text-red-400" data-testid="contact-error">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="group inline-flex w-full items-center justify-center gap-3 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-[0.16em] text-white transition-all active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                style={{ background: ACCENT }}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
                {submitting ? "Sending…" : "Send message"}
                {!submitting && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
            </form>

            <div className="mt-12 border-t border-white/[0.08] pt-8">
              <p className="text-sm text-white/55">Prefer email?</p>
              <a href="mailto:hello@adaptience.com" data-testid="contact-mailto" className="mt-1 inline-block text-base font-medium transition-colors hover:opacity-80" style={{ color: ACCENT }}>
                hello@adaptience.com
              </a>
            </div>
          </motion.div>
        )}
      </main>

      {/* Breathing room + shared site footer */}
      <div className="relative z-10 mx-auto w-full max-w-4xl px-6 pt-14 sm:px-10 sm:pt-24">
        <SiteFooter onPrivacy={onPrivacy} onTerms={onTerms} onContact={onContact} />
      </div>
    </div>
  );
}
